import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';

import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { flexRowCentered } from "../../sx/common";

import TextField from './TextField';
import FromToInterval from './FromToInterval';
import RecurringIntervalSelector from './recurringIntervalSelector/RecurringIntervalSelector';
import PrimaryButton from './core/PrimaryButton';
import RequestPeriodTabs from './RequestPeriodTabs';

import { addItemToCart, selectDisabledNonRecurringTab, selectDisabledRecurringTab } from '../../../store/cartSlice';
import {
    selectStartDate,
    selectEndDate,
    selectFromToHasErrors,
    selectOutsideWorkingHoursDropoff,
    selectOutsideWorkingHoursDropoffError,
    selectOutsideWorkingHoursPickup,
    selectOutsideWorkingHoursPickupError,
    selectRecurrenceHasErrors,
    selectRecurring
} from '../../../store/filtersSlice';
import { AvailabilityUtils } from '../../../store/utils/AvailabilityUtils';
import { selectPricesEnabled, selectShowRecurrentTab } from '../../../store/settingsSlice';

import { calculateArticlePrice } from '../../../utils/ArticlePrice';
import { ProductUtils } from '../../../store/utils/ProductUtils';
import { ValidationUtils } from '../../../utils/ValidationUtils';
import { useCartNotification } from '../../../hooks/useCartNotification';

export default function ReservationSection(props) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { showItemAddedToCart } = useCartNotification();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [quantityTouched, setQuantityTouched] = React.useState(false);

    const showRecurrentTab = useSelector(selectShowRecurrentTab);
    const recurringState = useSelector(selectRecurring);
    const hasRecurrenceErrorsState = useSelector(selectRecurrenceHasErrors);
    const outsideWorkingHoursPickupState = useSelector(selectOutsideWorkingHoursPickup);
    const outsideWorkingHoursPickupErrorState = useSelector(selectOutsideWorkingHoursPickupError);
    const outsideWorkingHoursDropoffState = useSelector(selectOutsideWorkingHoursDropoff);
    const outsideWorkingHoursDropoffErrorState = useSelector(selectOutsideWorkingHoursDropoffError);

    const hasFromToErrorsState = useSelector(selectFromToHasErrors);

    const nonRecurringTabDisabled = useSelector(selectDisabledNonRecurringTab);
    const recurringTabDisabled = useSelector(selectDisabledRecurringTab);

    const startDateState = useSelector(selectStartDate);
    const endDateState = useSelector(selectEndDate);

    const pricesEnabled = useSelector(selectPricesEnabled);

    const {
        direction,
        item = false,
        subarticle = false,
        isCartItem = false,
        showQuantity = false,
        onChangeQuantity = false,
        showAddToCartButton = false,
        showIntervalSelection = true,
        loadingAvailability = false,
        withAvailability = true
    } = props;

    const quantity = item && item.quantity ? item.quantity : "";
    const effectiveMaxQuantity = ValidationUtils.getEffectiveMaxQuantity(item);

    const isRecurring = subarticle?.recurring ?? item?.recurring ?? recurringState;
    const itemAvailability = subarticle?.available ?? item?.available ?? false;
    const { available, addToCartButtonLabel } = AvailabilityUtils.handleAvailabilityResponse(itemAvailability);

    const hasFromToErrors = subarticle?.hasFromToErrors || item?.hasFromToErrors || hasFromToErrorsState;
    const hasRecurrenceErrors = subarticle?.hasRecurrenceErrors ?? item?.hasRecurrenceErrors ?? hasRecurrenceErrorsState;
    
    const outsideWorkingHoursPickup = subarticle?.outsideWorkingHoursPickup ?? item?.outsideWorkingHoursPickup ?? outsideWorkingHoursPickupState;
    const outsideWorkingHoursPickupError = subarticle?.outsideWorkingHoursPickupError ?? item?.outsideWorkingHoursPickupError ?? outsideWorkingHoursPickupErrorState;
    const outsideWorkingHoursDropoff = subarticle?.outsideWorkingHoursDropoff ?? item?.outsideWorkingHoursDropoff ?? outsideWorkingHoursDropoffState;
    const outsideWorkingHoursDropoffError = subarticle?.outsideWorkingHoursDropoffError ?? item?.outsideWorkingHoursDropoffError ?? outsideWorkingHoursDropoffErrorState;

    const showTabsSelector = showRecurrentTab && showIntervalSelection;

    // For cart items, show error immediately. For product details, only show after user interaction
    const hasInvalidQuantity = showQuantity && (isCartItem || quantityTouched) && !ValidationUtils.isValidQuantity(quantity);
    const hasExceededMaxCount = showQuantity && effectiveMaxQuantity > 0 && ValidationUtils.isValidQuantity(quantity) && !ValidationUtils.isQuantityWithinMaxLimit(quantity, item);
    const quantityHasError = hasInvalidQuantity || hasExceededMaxCount;
    const resolvedAddToCartButtonLabel = hasExceededMaxCount ? "button.addToCart.disabled.max_count" : addToCartButtonLabel;
    const resolvedAddToCartButtonText = resolvedAddToCartButtonLabel === "button.addToCart.disabled.max_count" && effectiveMaxQuantity > 0
        ? t("button.addToCart.disabled.max_count_with_limit", { max: effectiveMaxQuantity })
        : t(resolvedAddToCartButtonLabel);

    const shouldDisableAddToCart = (!!(!isRecurring && hasFromToErrors) || !!(isRecurring && hasRecurrenceErrors) || quantityHasError || loadingAvailability || !available);

    const handleQuantityChange = (event) => {
        const newValue = event.target.value;
        setQuantityTouched(true);

        if (ValidationUtils.isAcceptableQuantityInput(newValue)) {
            onChangeQuantity(newValue);
        }
        // If value is invalid (0, negative, or non-numeric), don't update
    }

    const handleQuantityBlur = () => {
        setQuantityTouched(true);
    }

    const handleAddToCart = () => {
        dispatch(addItemToCart(item, subarticle));
        showItemAddedToCart();
    }

    useEffect(() => {
        let snackbarMessage = "";
        if (outsideWorkingHoursPickup && outsideWorkingHoursDropoff && outsideWorkingHoursPickupError !== '' && outsideWorkingHoursDropoffError !== '') {
            snackbarMessage = outsideWorkingHoursPickupError + "\n" + outsideWorkingHoursDropoffError
        } else {
            snackbarMessage = outsideWorkingHoursPickup ? outsideWorkingHoursPickupError : outsideWorkingHoursDropoffError;
        }

        if (isMobile && hasRecurrenceErrors) {
            (outsideWorkingHoursPickup || outsideWorkingHoursDropoff) && (snackbarMessage !== '') && enqueueSnackbar(snackbarMessage, {
                variant: 'error',
                preventDuplicate: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                style: { whiteSpace: 'pre-line' }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, item, subarticle, hasRecurrenceErrors, outsideWorkingHoursPickup, outsideWorkingHoursDropoff])

    let articlePrice = 0;

    const showPrice = pricesEnabled && (!isRecurring && (showAddToCartButton || isCartItem));

    if(subarticle) {
        articlePrice = calculateArticlePrice(subarticle.article_price, subarticle.price_unit, 
            subarticle.startDate ?? startDateState, subarticle.endDate ?? endDateState);
    } else if(item) {
        articlePrice = calculateArticlePrice(item.article_price, item.price_unit,
            item.startDate ?? startDateState, item.endDate ?? endDateState, quantity);
    }

    const hasColumnLayout = direction === 'column';

    return (
        <Stack direction='column' sx={hasColumnLayout ? { width: '100%' } : undefined}>
            {showTabsSelector && !(isCartItem && (nonRecurringTabDisabled || recurringTabDisabled)) &&
                <Box sx={hasColumnLayout ? {
                    width: '100%',
                    pb: 3,
                    '& .MuiTabs-root': {
                        width: '100%'
                    },
                    '& .MuiTabs-flexContainer': {
                        justifyContent: 'space-between'
                    },
                    '& .MuiTab-root': {
                        flex: '1 1 0',
                        minWidth: 0
                    }
                } : { pb: 3 }}>
                    <RequestPeriodTabs item={item} subarticle={subarticle} isCartItem={isCartItem} />
                </Box>}

            <Stack direction={direction ?? { xs: 'column', md: (isRecurring ? 'column' : 'row') }} spacing={2} sx={hasColumnLayout ? { width: '100%' } : undefined}>
            {showIntervalSelection &&
                <Box sx={hasColumnLayout ? {
                    width: '100%',
                    '& .MuiFormControl-root': {
                        width: '100%'
                    }
                } : undefined}>
                    {isRecurring ?
                        <RecurringIntervalSelector direction={direction} item={item} subarticle={subarticle} isCartItem={isCartItem} /> :
                        <FromToInterval direction={direction} withAvailability={withAvailability} item={item} subarticle={subarticle} isCartItem={isCartItem} />}
                </Box>
            }
        
                {hasColumnLayout ?
                    <Stack direction='column' spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
                        {(showQuantity || showPrice) &&
                            <Stack direction='row' spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                {showQuantity && <TextField sx={{ flex: '1 1 0', minWidth: 0 }}
                                    forceHelperText={isRecurring && !hasExceededMaxCount}
                                    name="quantity"
                                    label={t("input.label.quantity")}
                                    id="quantity"
                                    value={quantity}
                                    onChange={handleQuantityChange}
                                    onBlur={handleQuantityBlur}
                                    error={quantityHasError}
                                />}

                                {showPrice &&
                                    <Stack direction='column' sx={{ flex: '1 1 0', minWidth: 0 }}>
                                        <Typography variant="body1" color="text.primary">{ProductUtils.formatPrice(articlePrice)} €</Typography>
                                        <Typography variant="caption" color="text.primary">{t("vat_included")}</Typography>
                                    </Stack>
                                }
                            </Stack>
                        }

                        {(showAddToCartButton || (shouldDisableAddToCart && isCartItem && !(!!(!isRecurring && hasFromToErrors) || !!(isRecurring && hasRecurrenceErrors)))) &&
                            <Box sx={{ ...flexRowCentered, width: '100%', '& .MuiButton-root': { width: '100%' } }}>
                                <PrimaryButton disabled={shouldDisableAddToCart} label={(loadingAvailability && !quantityHasError) ? t("button.addToCartLoadingAvailability") : resolvedAddToCartButtonText} onClick={handleAddToCart} />
                            </Box>
                        }
                    </Stack> :
                    <Stack direction='row' spacing={2} alignItems="center">
                        {showQuantity && <TextField sx={{ width: 100 }}
                            forceHelperText={isRecurring && !hasExceededMaxCount}
                            name="quantity"
                            label={t("input.label.quantity")}
                            id="quantity"
                            value={quantity}
                            onChange={handleQuantityChange}
                            onBlur={handleQuantityBlur}
                            error={quantityHasError}
                        />}

                        {showPrice &&
                            <Stack direction='column'>
                                <Typography variant="body1" color="text.primary">{ProductUtils.formatPrice(articlePrice)} €</Typography>
                                <Typography variant="caption" color="text.primary">{t("vat_included")}</Typography>
                            </Stack>
                        }

                        {(showAddToCartButton || (shouldDisableAddToCart && isCartItem && !(!!(!isRecurring && hasFromToErrors) || !!(isRecurring && hasRecurrenceErrors)))) &&
                            <Box sx={flexRowCentered}>
                                <PrimaryButton disabled={shouldDisableAddToCart} label={(loadingAvailability && !quantityHasError) ? t("button.addToCartLoadingAvailability") : resolvedAddToCartButtonText} onClick={handleAddToCart} />
                            </Box>
                        }
                    </Stack>
                }
            </Stack>
        </Stack>
    );
};
